import { Link } from "react-router-dom";
import SEO from "@/components/base/SEO";
import Breadcrumb from "@/components/base/Breadcrumb";
import SchemaOrg from "@/components/base/SchemaOrg";
import { aboutContent } from "@/mocks/about";
import { siteConfig } from "@/mocks/site";

function AboutSchema() {
  const orgSchema = {
    "@type": "Organization",
    name: siteConfig.name,
    url: siteConfig.url,
    description: siteConfig.description,
    email: aboutContent.contact.email,
    address: {
      "@type": "PostalAddress",
      streetAddress: "港区六本木3-2-1 演艺大楼8F",
      addressLocality: "东京都",
      addressCountry: "JP",
    },
    contactPoint: {
      "@type": "ContactPoint",
      telephone: aboutContent.contact.phone,
      contactType: "customer service",
    },
  };

  const breadcrumbSchema = {
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "首页", item: siteConfig.url },
      { "@type": "ListItem", position: 2, name: "关于风间由美" },
    ],
  };

  return (
    <>
      <SchemaOrg type="Organization" data={orgSchema} />
      <SchemaOrg type="BreadcrumbList" data={breadcrumbSchema} />
    </>
  );
}

export default function About() {
  return (
    <>
      <SEO
        title={`关于风间由美 - ${siteConfig.name}`}
        description={`了解风间由美的个人简介、演艺生涯、口碑评价及联系方式。${siteConfig.description}`}
        keywords={`风间由美个人资料,风间由美简介,风间由美演艺经历,${siteConfig.keywords}`}
        canonicalUrl={`${siteConfig.url}/about`}
        ogType="website"
        ogImage={aboutContent.heroImage}
      />
      <AboutSchema />

      {/* Hero */}
      <section className="relative w-full h-[300px] md:h-[400px] overflow-hidden">
        <img
          src={aboutContent.heroImage}
          alt="关于风间由美"
          className="absolute inset-0 w-full h-full object-cover object-top"
          title="风间由美 关于"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/50"></div>
        <div className="relative z-10 w-full h-full flex flex-col items-center justify-center text-center px-4">
          <h1
            className="text-3xl md:text-4xl font-semibold text-white"
            style={{ fontFamily: "var(--font-heading)" }}
          >
            {aboutContent.heroTitle}
          </h1>
          <p className="text-white/80 text-sm md:text-base mt-2">{aboutContent.heroSubtitle}</p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 md:px-6 pt-6 pb-14">
        <Breadcrumb
          items={[
            { label: "首页", path: "/" },
            { label: "关于风间由美" },
          ]}
        />

        {/* Bio Section */}
        <section className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-10">
          <div className="lg:col-span-2">
            <h2
              className="text-2xl font-semibold text-foreground-950 mb-6"
              style={{ fontFamily: "var(--font-heading)" }}
            >
              {aboutContent.bio.title}
            </h2>
            <div className="text-sm text-foreground-700 leading-relaxed space-y-4 whitespace-pre-line">
              {aboutContent.bio.content}
            </div>
          </div>

          <div className="lg:col-span-1">
            <div className="bg-background-100 border border-background-200/70 rounded-lg p-6">
              <h3 className="text-base font-semibold text-foreground-950 mb-4">社交媒体</h3>
              <div className="flex flex-col gap-3">
                {aboutContent.contact.socialLinks.map((link) => (
                  <a
                    key={link.name}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 text-sm text-foreground-600 hover:text-primary-500 transition-colors cursor-pointer"
                  >
                    <i className={`${link.icon} text-lg`}></i>
                    {link.name}
                  </a>
                ))}
              </div>

              <hr className="my-5 border-background-200/60" />

              <h3 className="text-base font-semibold text-foreground-950 mb-4">联系方式</h3>
              <div className="flex flex-col gap-3 text-sm">
                <div className="flex items-start gap-3">
                  <i className="ri-mail-line text-foreground-500 mt-0.5"></i>
                  <span className="text-foreground-600">{aboutContent.contact.email}</span>
                </div>
                <div className="flex items-start gap-3">
                  <i className="ri-phone-line text-foreground-500 mt-0.5"></i>
                  <span className="text-foreground-600">{aboutContent.contact.phone}</span>
                </div>
                <div className="flex items-start gap-3">
                  <i className="ri-map-pin-line text-foreground-500 mt-0.5"></i>
                  <span className="text-foreground-600">{aboutContent.contact.address}</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Milestones */}
        <section className="mt-16">
          <h2
            className="text-2xl font-semibold text-foreground-950 mb-8 text-center"
            style={{ fontFamily: "var(--font-heading)" }}
          >
            演艺生涯里程碑
          </h2>
          <div className="relative">
            <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-background-200/70 md:-translate-x-px"></div>
            <div className="flex flex-col gap-6">
              {aboutContent.milestones.map((milestone, index) => (
                <div
                  key={milestone.year}
                  className={`relative flex items-start gap-6 ${
                    index % 2 === 0 ? "md:flex-row" : "md:flex-row-reverse"
                  }`}
                >
                  <div className="hidden md:block md:w-1/2"></div>
                  <div className="absolute left-4 md:left-1/2 w-3 h-3 rounded-full bg-accent-500 border-2 border-background-50 -translate-x-1/2 mt-1.5 z-10"></div>
                  <div className="ml-10 md:ml-0 md:w-1/2 bg-background-50 border border-background-200/70 rounded-lg p-5">
                    <span className="text-xs font-semibold text-accent-500 bg-accent-100 px-2 py-0.5 rounded-md whitespace-nowrap">
                      {milestone.year}
                    </span>
                    <h4 className="text-sm font-medium text-foreground-950 mt-2">{milestone.title}</h4>
                    <p className="text-xs text-foreground-600 mt-1 leading-relaxed">
                      {milestone.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section className="mt-16">
          <h2
            className="text-2xl font-semibold text-foreground-950 mb-8 text-center"
            style={{ fontFamily: "var(--font-heading)" }}
          >
            业内评价
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {aboutContent.testimonials.map((testimonial, index) => (
              <div
                key={index}
                className="bg-background-50 border border-background-200/70 rounded-lg p-6"
              >
                <div className="flex items-center gap-2 mb-3">
                  <i className="ri-double-quotes-l text-2xl text-accent-500/40"></i>
                </div>
                <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                  {testimonial.content}
                </p>
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-secondary-100 flex items-center justify-center">
                    <span className="text-xs font-medium text-secondary-700">
                      {testimonial.name.charAt(0)}
                    </span>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground-950">{testimonial.name}</p>
                    <p className="text-xs text-foreground-500">{testimonial.title}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Contact Form CTA */}
        <section className="mt-16 text-center py-12 bg-background-100 border border-background-200/70 rounded-lg">
          <h2
            className="text-xl font-semibold text-foreground-950 mb-3"
            style={{ fontFamily: "var(--font-heading)" }}
          >
            商务合作与媒体咨询
          </h2>
          <p className="text-sm text-foreground-600 mb-6 max-w-md mx-auto">
            如有商务合作、媒体采访或其他工作事宜，请通过以下方式联系我们
          </p>
          <Link
            to="/about#contact"
            className="inline-flex items-center gap-2 bg-primary-500 text-background-50 px-6 py-3 rounded-md text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-mail-send-line"></i> 联系我们
          </Link>
        </section>
      </div>
    </>
  );
}